import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchQueues } from '../lib/queries.ts';
import type { QueueJobSummary, QueueSummary } from '../lib/types.ts';

/** An active job older than this is almost certainly hung (renders take
 *  minutes, not half-hours) — flag it so the fix (worker restart) is obvious. */
const STUCK_AFTER_SEC = 30 * 60;
const REFRESH_MS = 5_000;

const COUNT_COLORS = {
  active: '#2563eb',
  waiting: '#6b7280',
  failed: '#dc2626',
  delayed: '#d97706',
  completed: '#16a34a',
} as const;
type CountKind = keyof typeof COUNT_COLORS;

function fmtAge(sec: number | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${sec % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtTime(ms: number | null): string {
  return ms ? new Date(ms).toLocaleString() : '—';
}

function fmtPayload(data: unknown): string {
  if (data && typeof data === 'object') {
    return Object.entries(data as Record<string, unknown>)
      .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
      .join(' · ');
  }
  return data == null ? '' : String(data);
}

function activity(q: QueueSummary): number {
  return q.counts.active * 1000 + q.counts.waiting * 10 + q.counts.failed;
}

export function JobsPage(): React.ReactElement {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: ['admin-queues'], queryFn: fetchQueues, refetchInterval: REFRESH_MS });
  const queues = [...(query.data?.queues ?? [])].sort((a, b) => activity(b) - activity(a));
  const generatedAt = query.data ? new Date(query.data.generatedAt).toLocaleTimeString() : '—';
  const busy = queues.reduce((n, q) => n + q.counts.active + q.counts.waiting, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('jobs.title')}</h1>
          <div className="sub">{t('jobs.subtitle', { busy, time: generatedAt })}</div>
        </div>
      </div>

      {query.isError && (
        <div className="section">
          <div className="section-body" style={{ padding: 12, color: '#dc2626' }}>
            {t('jobs.loadError')}: {query.error instanceof Error ? query.error.message : String(query.error)}
          </div>
        </div>
      )}

      {queues.map((q) => (
        <QueueCard key={q.key} queue={q} />
      ))}
    </>
  );
}

function CountChip({ kind, n }: { kind: CountKind; n: number }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 12,
        marginRight: 6,
        background: COUNT_COLORS[kind],
        color: '#fff',
        opacity: n === 0 ? 0.35 : 1,
      }}
    >
      {n} {t(`jobs.${kind}`)}
    </span>
  );
}

function QueueCard({ queue }: { queue: QueueSummary }): React.ReactElement {
  const { t } = useTranslation();
  const idle = queue.active.length === 0 && queue.waiting.length === 0 && queue.failed.length === 0;
  return (
    <div className="section">
      <div
        className="section-head"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', gap: 12, flexWrap: 'wrap' }}
      >
        <strong style={{ fontFamily: 'monospace' }}>
          {queue.name}
          {queue.paused && <span style={{ marginLeft: 8, fontSize: 12, color: '#d97706' }}>⏸ {t('jobs.paused')}</span>}
        </strong>
        <div>
          <CountChip kind="active" n={queue.counts.active} />
          <CountChip kind="waiting" n={queue.counts.waiting} />
          <CountChip kind="delayed" n={queue.counts.delayed} />
          <CountChip kind="failed" n={queue.counts.failed} />
          <CountChip kind="completed" n={queue.counts.completed} />
        </div>
      </div>
      {!idle && (
        <div className="section-body" style={{ padding: '0 12px 12px' }}>
          <JobTable title={t('jobs.activeJobs')} jobs={queue.active} kind="active" />
          <JobTable title={t('jobs.waitingJobs')} jobs={queue.waiting} kind="waiting" />
          <JobTable title={t('jobs.failedJobs')} jobs={queue.failed} kind="failed" />
        </div>
      )}
    </div>
  );
}

function JobTable({
  title,
  jobs,
  kind,
}: {
  title: string;
  jobs: QueueJobSummary[];
  kind: 'active' | 'waiting' | 'failed';
}): React.ReactElement | null {
  const { t } = useTranslation();
  if (jobs.length === 0) return null;
  const cell: React.CSSProperties = {
    padding: '6px 8px',
    verticalAlign: 'top',
    borderTop: '1px solid rgba(128,128,128,0.2)',
    fontSize: 13,
  };
  const ageHeader = kind === 'active' ? t('jobs.runningFor') : kind === 'waiting' ? t('jobs.waitingFor') : t('jobs.failedAt');
  return (
    <div style={{ marginTop: 10 }}>
      <div className="sub" style={{ marginBottom: 4 }}>
        {title}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ fontSize: 12, opacity: 0.7, textAlign: 'left' }}>
            <th style={cell}>#</th>
            <th style={cell}>{t('jobs.payload')}</th>
            <th style={cell}>{ageHeader}</th>
            <th style={cell}>{kind === 'failed' ? t('jobs.reason') : t('jobs.attempts')}</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => {
            const stuck = kind === 'active' && j.ageSec != null && j.ageSec > STUCK_AFTER_SEC;
            return (
              <tr key={j.id} style={stuck ? { background: 'rgba(220,38,38,0.08)' } : undefined}>
                <td style={{ ...cell, fontFamily: 'monospace' }}>{j.id}</td>
                <td style={{ ...cell, fontFamily: 'monospace', wordBreak: 'break-all' }}>{fmtPayload(j.data)}</td>
                <td style={cell}>
                  {kind === 'failed' ? fmtTime(j.finishedAt) : fmtAge(j.ageSec)}
                  {stuck && (
                    <div style={{ color: '#dc2626', fontSize: 12 }}>⚠ {t('jobs.stuckHint', { min: STUCK_AFTER_SEC / 60 })}</div>
                  )}
                </td>
                <td style={{ ...cell, wordBreak: 'break-word' }}>
                  {kind === 'failed' ? (j.failedReason ?? '—') : String(j.attemptsMade + (kind === 'active' ? 1 : 0))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
